import React, { useState, useEffect, useCallback } from 'react';
import './ImageGallery.css';

interface ImageGalleryProps {
  images: string[];
  /** Function to get full URL for an image (handles relative paths) */
  getImageUrl?: (url: string) => string;
  /** Alt text prefix for images */
  altPrefix?: string;
}

/**
 * Image gallery component with lightbox functionality.
 * - Click on thumbnail to open lightbox
 * - Navigate with arrows (on-screen and keyboard)
 * - Press Escape to close
 */
const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  getImageUrl = (url) => url,
  altPrefix = 'Gallery image',
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxOpen, closeLightbox, goToPrevious, goToNext]);

  if (images.length === 0) return null;

  return (
    <>
      {/* Thumbnail Grid */}
      <div className="image-gallery-grid">
        {images.map((url, index) => (
          <button
            key={index}
            className="image-gallery-thumbnail"
            onClick={() => openLightbox(index)}
            aria-label={`${altPrefix} ${index + 1}, click to enlarge`}
          >
            <img
              src={getImageUrl(url)}
              alt={`${altPrefix} ${index + 1}`}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="image-gallery-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          {/* Close button */}
          <button
            className="image-gallery-close"
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            &times;
          </button>

          {/* Previous arrow */}
          {images.length > 1 && (
            <button
              className="image-gallery-nav image-gallery-prev"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              aria-label="Previous image"
            >
              &#8249;
            </button>
          )}

          {/* Main image */}
          <div
            className="image-gallery-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getImageUrl(images[currentIndex])}
              alt={`${altPrefix} ${currentIndex + 1}`}
            />
            {images.length > 1 && (
              <div className="image-gallery-counter">
                {currentIndex + 1} / {images.length}
              </div>
            )}
          </div>

          {/* Next arrow */}
          {images.length > 1 && (
            <button
              className="image-gallery-nav image-gallery-next"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              aria-label="Next image"
            >
              &#8250;
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default ImageGallery;
